/* eslint-disable @typescript-eslint/no-unsafe-call */
/* eslint-disable @typescript-eslint/no-unsafe-return */
/* eslint-disable @typescript-eslint/no-unsafe-member-access */
import { AuthUserProfileStore } from '@arc/arc-shared-frontend';
import { createSelector } from '@ngrx/store';
import {
  AppliedDiscountMeta,
  BtPayContext,
  getPadTwikeyRedirectCheckoutFlow,
  mapOriginatingApplication,
  ModifiedDocumentMeta,
  ModifiedSessionDocumentInfo,
  OriginSystem,
  PaymentInformationDocument,
  PaymentStatus,
  PayOtherPayables,
  roundTo2Decimals,
  SelectPaymentAccountsRequestData,
  SessionDocumentInfo,
  SessionDocumentType,
  SessionType,
} from '@arc/unified-payments-frontend/shared-payment';
import {
  BtpaySessionStore,
  SelectedAppliedDiscountMetaStore,
  SelectedModifiedDocumentBurnsStore,
  SessionAccountInfoStore,
  SessionStore,
} from '../reducers/session.reducer';
import { SelectPayOtherAmountStore } from '../../btpay/reducers/btpay.reducers';
import {
  selectSelectedDocuments,
  selectSessionDocuments,
  selectSortedSessionDocuments,
} from './session-documents.selectors';
import { selectTenantId, selectTenantName } from '../../external-selectors';

export const selectSubTotal = createSelector(selectSelectedDocuments, (state) =>
  roundTo2Decimals(
    state
      ?.filter((d) => d.isPayable && d.payableAmount > 0)
      .map((x) => x.payableAmount)
      .reduce((acc, curr) => acc + curr, 0),
  ),
);

export const selectSubTotalByBuyerAccount = createSelector(
  selectSessionDocuments,
  (state) => {
    const result = new Map<string, number>();
    state
      ?.filter((d) => d.isPayable && d.payableAmount > 0)
      .forEach((doc) => {
        const currentTotal = result.get(doc.buyerAccount) || 0;
        result.set(
          doc.buyerAccount,
          roundTo2Decimals(currentTotal + doc.payableAmount),
        );
      });
    return result;
  },
);

export const selectAppliedDiscountSubTotal = createSelector(
  selectSelectedDocuments,
  (state) =>
    roundTo2Decimals(
      state
        ?.filter((d) => d.isPayable && d.appliedDiscountAmount > 0)
        .map((x) => {
          return x.appliedDiscountAmount;
        })
        .reduce((acc, curr) => acc + curr, 0),
    ),
);

export const selectCreditMemoSubtotal = createSelector(
  selectSelectedDocuments,
  (state) => {
    return roundTo2Decimals(
      state
        ?.filter((d) => d.isPayable && d.payableAmount < 0)
        .map((x) => {
          return x.payableAmount;
        })
        .reduce((acc, curr) => acc + curr, 0),
    );
  },
);

export const selectCreditMemoSubtotalByBuyerAccount = createSelector(
  selectSessionDocuments,
  (state) => {
    const result = new Map<string, number>();
    state
      ?.filter((d) => d.isPayable && d.payableAmount < 0)
      .forEach((doc) => {
        const currentTotal = result.get(doc.buyerAccount) || 0;
        result.set(
          doc.buyerAccount,
          roundTo2Decimals(currentTotal + doc.payableAmount),
        );
      });
    return result;
  },
);

export const selectTotalAmount = createSelector(
  selectSubTotal,
  selectCreditMemoSubtotal,
  (subTotal, creditMemoSubTotal) => {
    return roundTo2Decimals(subTotal + (creditMemoSubTotal ?? 0));
  },
);

export const selectTotalAmountByBuyerAccount = createSelector(
  selectSubTotalByBuyerAccount,
  selectCreditMemoSubtotalByBuyerAccount,
  (subTotalByBuyerAccount, creditMemoSubTotalByBuyerAccount) => {
    const result = new Map<string, number>();

    subTotalByBuyerAccount.forEach((subTotal, buyerAccount) => {
      const creditMemoSubTotal =
        creditMemoSubTotalByBuyerAccount.get(buyerAccount) || 0;
      result.set(buyerAccount, roundTo2Decimals(subTotal + creditMemoSubTotal));
    });

    return result;
  },
);

export const selectIsZeroDollarPayment = createSelector(
  selectTotalAmount,
  (total) => {
    return total === 0;
  },
);

export const selectModifiedDocuments = createSelector(
  selectSortedSessionDocuments,
  SelectedModifiedDocumentBurnsStore,
  SelectedAppliedDiscountMetaStore,
  SelectPayOtherAmountStore,
  SessionStore,
  (
    stateDocuments,
    stateModifiedDocumentBurns,
    stateAppliedDiscountMeta,
    statePayOtherAmount,
    stateSessionSummary,
  ) => {
    const docs: SessionDocumentInfo[] = stateDocuments;
    const docMeta: ModifiedDocumentMeta[] = stateModifiedDocumentBurns;
    const discountMeta: AppliedDiscountMeta[] = stateAppliedDiscountMeta;
    const payOtherAmount = statePayOtherAmount;
    // eslint-disable-next-line prefer-const
    let payload: ModifiedSessionDocumentInfo[] = [];

    docs?.forEach((document) => {
      const discMeta = discountMeta?.find(
        (x: AppliedDiscountMeta) => x.urn === document.urn,
      );
      const originalAmount =
        discMeta?.discountedPayableAmount ?? document.originalPayableAmount;
      const isModified = docMeta?.some(
        (x: ModifiedDocumentMeta) =>
          x.urn === document.urn && originalAmount !== document.payableAmount,
      );

      let payOtherDocument: PaymentInformationDocument = undefined;
      payOtherAmount?.payables?.forEach((x: PayOtherPayables) => {
        for (const element of x.documents) {
          if (element.burn === document.urn) {
            payOtherDocument = element;
            break;
          }
        }
      });

      payload.push({
        ...document,
        payOtherReasonCode: payOtherDocument?.reasonCode,
        modified: isModified,
        isPayableAmountEditable: false,
        discountState: discMeta?.discountState,
        originalPayableAmount:
          stateSessionSummary.sessionType === SessionType.PayOtherAmount
            ? payOtherDocument?.originalPayableAmount || 0
            : document.originalPayableAmount,
        originalSessionPayableAmount:
          stateSessionSummary.sessionType === SessionType.PayOtherAmount
            ? payOtherDocument?.realOpenBalance || 0
            : document.originalSessionPayableAmount,
      });
    });
    return payload;
  },
);

export const selectSelectedModifiedDocuments = createSelector(
  selectModifiedDocuments,
  (modifiedDocuments: ModifiedSessionDocumentInfo[]) => {
    return modifiedDocuments?.filter((md) => md.isPayable && md.isSelected);
  },
);

export const selectSelectedNonZeroModifiedDocuments = createSelector(
  selectSelectedModifiedDocuments,
  (modifiedDocuments: ModifiedSessionDocumentInfo[]) => {
    return modifiedDocuments?.filter((md) => md.payableAmount != 0);
  },
);

export const selectAreThereZeroDollarPayables = createSelector(
  selectSelectedModifiedDocuments,
  (modifiedDocuments: ModifiedSessionDocumentInfo[]) => {
    return modifiedDocuments?.some((md) => md.payableAmount == 0);
  },
);

export const selectAreThereZeroDollarStatements = createSelector(
  selectSelectedModifiedDocuments,
  (modifiedDocuments: ModifiedSessionDocumentInfo[]) => {
    return modifiedDocuments?.some(
      (md) =>
        (md.documentType as SessionDocumentType) ===
          SessionDocumentType.Statement && md.payableAmount == 0,
    );
  },
);

export const selectIsDiscountAmountVisible = createSelector(
  selectSelectedModifiedDocuments,
  (modifiedDocuments: ModifiedSessionDocumentInfo[]) => {
    return !!modifiedDocuments?.some(
      (md) => !!md.discountState && !md.isCreditMemo,
    );
  },
);

export const selectSessionId = createSelector(SessionStore, (session) => {
  return session.sessionId;
});

const selectVirtualTenantId = createSelector(
  SessionStore,
  (state) => state?.virtualTenantId,
);

export const selectVirtualOtherwisePrimaryTenantId = createSelector(
  selectVirtualTenantId,
  selectTenantId,
  (virtualTenantId, tenantId) => {
    return virtualTenantId || tenantId;
  },
);

export const selectSessionBuyerAccountIdentifier = createSelector(
  SessionStore,
  (session) => {
    return session?.buyerAccountIdentifier;
  },
);

export const selectSessionBuyerAccountIdentifierWithoutPrefix = createSelector(
  selectSessionBuyerAccountIdentifier,
  (buyerAccountIdentifier) => {
    return buyerAccountIdentifier?.replace('accountNumber:', '');
  },
);

export const selectCurrencyCode = createSelector(SessionStore, (state) =>
  state?.payables?.length && state?.payables[0].documents?.length
    ? state.payables[0].documents[0].currencyCode
    : state?.currencyCode,
);

export const selectCurrencyCodeFromSession = createSelector(
  SessionStore,
  (state) => state?.currencyCode,
);

export const selectSessionPaymentProcess = createSelector(
  SessionStore,
  (session) => {
    return session?.paymentProcess;
  },
);

const originSystemMap = new Map<string, OriginSystem>([
  ['clientconnect', OriginSystem.bandp],
  ['collections', OriginSystem.bandp],
  ['eic', OriginSystem.bandp],
  ['invoicing', OriginSystem.bandp],
  ['bpn', OriginSystem.bpn],
]);

export const selectOriginSystem = createSelector(SessionStore, (state) =>
  originSystemMap.get(state?.originatingApplication.toLowerCase()),
);

export const selectOriginatingApplication = createSelector(
  SessionStore,
  (session) => session?.originatingApplication,
);

export const selectPayment = createSelector(
  SessionStore,
  (state) => state?.payment,
);

export const selectSessionPaymentReference = createSelector(
  selectPayment,
  (payment) => {
    return payment?.paymentReference;
  },
);

export const selectTransactions = createSelector(
  selectPayment,
  (payment) =>
    payment?.transactions?.slice().sort((n1, n2) => {
      if (n1.buyerAccountIdentifier > n2.buyerAccountIdentifier) {
        return 1;
      }

      if (n1.buyerAccountIdentifier < n2.buyerAccountIdentifier) {
        return -1;
      }

      return 0;
    }),
);

export const selectHasMultipleTransactions = createSelector(
  selectTransactions,
  (transactions) => transactions?.length > 1,
);

export const selectTransactionBuyerAccounts = createSelector(
  selectTransactions,
  (transactions) =>
    transactions?.map((t) =>
      t.buyerAccountIdentifier.replace('accountNumber:', ''),
    ),
);

export const selectTransactionsTotal = createSelector(
  selectTransactions,
  (transactions) =>
    roundTo2Decimals(
      transactions
        ?.filter((t) => t.status !== PaymentStatus.FAILED)
        .map((t) => t.totalAmount)
        .reduce((a, b) => a + b, 0) ?? 0,
    ),
);

export const selectTransactionsSubTotal = createSelector(
  selectTransactions,
  (transactions) =>
    roundTo2Decimals(
      transactions
        ?.filter((t) => t.status !== PaymentStatus.FAILED)
        .map((t) => t.payables)
        .reduce((a, b) => a.concat(b), [])
        .filter((p) => p.transactionAmount > 0)
        .map((p) => p.transactionAmount)
        .reduce((a, b) => a + b, 0) ?? 0,
    ),
);

export const selectTransactionsCreditTotal = createSelector(
  selectTransactions,
  (transactions) =>
    roundTo2Decimals(
      transactions
        ?.filter((t) => t.status !== PaymentStatus.FAILED)
        .map((t) => t.payables)
        .reduce((a, b) => a.concat(b), [])
        .filter((p) => p.transactionAmount < 0)
        .map((p) => p.transactionAmount)
        .reduce((a, b) => a + b, 0) ?? 0,
    ),
);

export const selectTransactionsAppliedDiscountTotal = createSelector(
  selectTransactions,
  (transactions) =>
    roundTo2Decimals(
      transactions
        ?.filter((t) => t.status !== PaymentStatus.FAILED)
        .map((t) => t.payables)
        .reduce((a, b) => a.concat(b), [])
        .map((p) => ((p.appliedDiscount ?? 0) > 0 ? p.appliedDiscount : 0))
        .reduce((a, b) => a + b, 0) ?? 0,
    ),
);

export const selectTransactionsSurchargeTotal = createSelector(
  selectTransactions,
  (transactions) =>
    roundTo2Decimals(
      transactions
        ?.filter((t) => t.status !== PaymentStatus.FAILED)
        .map((t) => t.totalSurcharge ?? 0)
        .reduce((a, b) => a + b, 0) ?? 0,
    ),
);

export const selectIsAutopaySupported = createSelector(
  SessionStore,
  (state) => {
    return state?.paymentProcess === 'Twikey';
  },
);

export const selectRecipients = createSelector(SessionStore, (state) => {
  return state?.contacts?.filter((c) => typeof c === 'string' && c !== '');
});

export const selectOnSessionCompleteUrl = createSelector(
  SessionStore,
  (state) => {
    return state?.onSessionCompleteUrl;
  },
);

export const selectOnSessionExitUrl = createSelector(SessionStore, (state) => {
  return state?.onSessionExitUrl ?? state?.onSessionCompleteUrl;
});

export const selectIsBuyerOriginApp = createSelector(SessionStore, (state) => {
  return state?.isBuyerOriginApp;
});

export const selectDisplayPrimaryEmail = createSelector(
  selectIsBuyerOriginApp,
  selectRecipients,
  (isBuyerOriginApp, recipients) => {
    return isBuyerOriginApp && recipients?.length > 0;
  },
);

export const selectNameOnAccount = createSelector(
  selectIsBuyerOriginApp,
  SessionAccountInfoStore,
  AuthUserProfileStore,
  (isBuyerOriginApp, accountInfo, user) => {
    // Buyer Flow
    if (isBuyerOriginApp) {
      return `${user.firstName} ${user.lastName}`;
    }

    // Suplier Flow
    return accountInfo?.name;
  },
);

export const selectSignedInDisplayName = createSelector(
  selectIsBuyerOriginApp,
  SessionAccountInfoStore,
  AuthUserProfileStore,
  (isBuyerOriginApp, accountInfo, user) => {
    // Buyer Flow
    if (isBuyerOriginApp) {
      const firstName = user.firstName === 'NA' ? '' : user.firstName ?? '';
      const lastName = user.lastName === 'NA' ? '' : user.lastName ?? '';
      return [firstName, lastName].filter(Boolean).join(' ');
    }

    // Suplier Flow
    return accountInfo?.name;
  },
);

export const selectInvoiceTypeCount = createSelector(
  selectSelectedDocuments,
  (documents) => {
    const invoiceCount = documents?.filter((x) => x.documentType === 'invoice')
      .length;
    return invoiceCount;
  },
);

export const selectCreditMemoTypeCount = createSelector(
  selectSelectedDocuments,
  (documents) => {
    const creditMemoCount = documents?.filter(
      (x) => x.documentType === 'creditmemo',
    ).length;
    return creditMemoCount;
  },
);

export const selectBtPayContext = createSelector(
  selectOriginatingApplication,
  (originatingApplication) => {
    return {
      application: originatingApplication,
      paymentAccountsSources: [
        OriginSystem[mapOriginatingApplication(originatingApplication)],
      ],
    } as BtPayContext;
  },
);

export const selectPaymentAccountsRequestData = createSelector(
  SessionStore,
  selectBtPayContext,
  (session, btPayContext) => {
    const requestData: SelectPaymentAccountsRequestData = {
      buyerAccountIdentifier: session.buyerAccountIdentifier,
      btPayContext,
      externalUserUrn: session.externalUserUrn,
    };

    return requestData;
  },
);

export const selectDocumentsBuyerAccountNumbers = createSelector(
  selectSelectedDocuments,
  (documents) => {
    return documents
      ?.map((p) => p.buyerAccount)
      ?.filter((value) => !!value)
      ?.filter((value, index, array) => array.indexOf(value) === index);
  },
);

export const selectIsSingleBuyerAccount = createSelector(
  selectDocumentsBuyerAccountNumbers,
  (buyerAccountNumbers) => {
    return buyerAccountNumbers?.length === 1;
  },
);

export const selectBuyerCompanyName = createSelector(
  SessionStore,
  (state) => state?.buyerCompanyName,
);

export const selectIsPaymentLinks = createSelector(
  selectOriginatingApplication,
  (originatingApplication) => {
    return originatingApplication === 'PAYMENTLINKS';
  },
);

export const selectIsHostedPaymentsValidatingFlow = createSelector(
  selectOriginatingApplication,
  (originatingApplication) => {
    return originatingApplication === 'HOSTEDPAYMENTSVALIDATING';
  },
);

export const selectCreditCardMerchantAccounts = createSelector(
  SessionStore,
  (state) => state?.creditCardMerchantAccounts,
);

export const selectSelectedModifiedDocumentsWithConfirmationNumber =
  createSelector(
    selectTransactions,
    selectSelectedNonZeroModifiedDocuments,
    (transactions, documents) => {
      return documents.map((document) => {
        const matchingTransaction = transactions.find((transaction) =>
          transaction.payables.find((payable) => payable.urn === document.urn),
        );
        return {
          ...document,
          confirmationNumber: matchingTransaction.transactionReference,
        };
      });
    },
  );

export const selectExternalUserUrn = createSelector(
  SessionStore,
  (state) => state?.externalUserUrn,
);

export const selectIsUnauthenticatedPaymentLinksUser = createSelector(
  selectIsPaymentLinks,
  selectExternalUserUrn,
  (isPaymentLinks, externalUserUrn) =>
    isPaymentLinks && externalUserUrn == null,
);

export const selectIsCountryCanada = createSelector(
  SessionStore,
  (state) => state?.country == 'CA',
);

export const selectCountry = createSelector(
  SessionStore,
  (state) => state?.country ?? 'US',
);

export const selectSessionType = createSelector(
  SessionStore,
  (state) => state?.sessionType,
);

export const selectPadTwikeyRedirectToken = createSelector(
  selectVirtualOtherwisePrimaryTenantId,
  selectSessionId,
  selectOriginatingApplication,
  selectSessionType,
  selectTenantName,
  (tenantId, sessionId, originatingApplication, sessionType, supplierName) => {
    const padRedirectCheckoutFlow = getPadTwikeyRedirectCheckoutFlow(
      sessionType,
      originatingApplication,
    );
    return `${padRedirectCheckoutFlow}${tenantId}${sessionId}${supplierName}`;
  },
);
